import React from "react";
import {
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Button,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  Spinner,
} from "@heroui/react";
import {
  DotsThreeVertical,
  Eye,
  PencilSimple,
  Trash,
} from "@phosphor-icons/react";
import { DocumentTemplate, DocumentCategory } from "./types";

interface DocumentTemplatesTableProps {
  templates: DocumentTemplate[];
  isLoading: boolean;
  onPreview: (template: DocumentTemplate) => void;
  onEdit: (template: DocumentTemplate) => void;
  onDelete: (template: DocumentTemplate) => void;
}

const DocumentTemplatesTable: React.FC<DocumentTemplatesTableProps> = ({
  templates,
  isLoading,
  onPreview,
  onEdit,
  onDelete,
}) => {
  const getCategoryColor = (category: DocumentCategory) => {
    switch (category) {
      case DocumentCategory.ACQUISITION:
        return "primary";
      case DocumentCategory.CONTRACT:
        return "success";
      case DocumentCategory.INSPECTION:
        return "warning";
      case DocumentCategory.LEGAL:
        return "danger";
      case DocumentCategory.RENOVATION:
        return "secondary";
      case DocumentCategory.GENERAL:
        return "default";
      default:
        return "default";
    }
  };

  const getCategoryLabel = (category: DocumentCategory) => {
    switch (category) {
      case DocumentCategory.ACQUISITION:
        return "Acquisitie";
      case DocumentCategory.CONTRACT:
        return "Contract";
      case DocumentCategory.INSPECTION:
        return "Inspectie";
      case DocumentCategory.LEGAL:
        return "Juridisch";
      case DocumentCategory.RENOVATION:
        return "Renovatie";
      case DocumentCategory.GENERAL:
        return "Algemeen";
      default:
        return category;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("nl-BE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Spinner size="lg" />
      </div>
    );
  }

  if (templates.length === 0) {
    return (
      <div className="p-8 text-center">
        <p className="text-neutral-secondaryMid">
          Geen document sjablonen gevonden
        </p>
        <p className="text-neutral-secondaryMid mt-2 text-sm">
          Klik op "Nieuw sjabloon" om je eerste sjabloon toe te voegen
        </p>
      </div>
    );
  }

  return (
    <Table aria-label="Document templates table">
      <TableHeader>
        <TableColumn>NAAM</TableColumn>
        <TableColumn>CATEGORIE</TableColumn>
        <TableColumn>PLACEHOLDERS</TableColumn>
        <TableColumn>CONTEXT</TableColumn>
        <TableColumn>GEMAAKT</TableColumn>
        <TableColumn>ACTIES</TableColumn>
      </TableHeader>
      <TableBody>
        {templates.map((template) => (
          <TableRow key={template.id}>
            <TableCell>
              <div>
                <p className="text-neutral-primary font-medium">
                  {template.name}
                </p>
                <p className="text-neutral-secondaryMid text-sm">
                  {template.templateKey}
                </p>
                {template.description && (
                  <p className="text-neutral-secondaryMid mt-1 text-xs">
                    {template.description}
                  </p>
                )}
              </div>
            </TableCell>
            <TableCell>
              <Chip
                size="sm"
                color={getCategoryColor(template.category)}
                variant="flat"
              >
                {getCategoryLabel(template.category)}
              </Chip>
            </TableCell>
            <TableCell>
              <div className="flex flex-wrap gap-1">
                {template.availablePlaceholders
                  .slice(0, 3)
                  .map((placeholder) => (
                    <Chip key={placeholder} size="sm" variant="bordered">
                      {placeholder}
                    </Chip>
                  ))}
                {template.availablePlaceholders.length > 3 && (
                  <Chip size="sm" variant="bordered">
                    +{template.availablePlaceholders.length - 3}
                  </Chip>
                )}
              </div>
            </TableCell>
            <TableCell>
              <div className="flex flex-wrap gap-1">
                {template.requiredContext.map((context) => (
                  <Chip key={context} size="sm" variant="dot" color="secondary">
                    {context}
                  </Chip>
                ))}
              </div>
            </TableCell>
            <TableCell>
              <span className="text-neutral-secondaryMid text-sm">
                {formatDate(template.createdAt)}
              </span>
            </TableCell>
            <TableCell>
              <Dropdown>
                <DropdownTrigger>
                  <Button isIconOnly size="sm" variant="light">
                    <DotsThreeVertical size={16} />
                  </Button>
                </DropdownTrigger>
                <DropdownMenu>
                  <DropdownItem
                    key="preview"
                    startContent={<Eye size={16} />}
                    onPress={() => onPreview(template)}
                  >
                    Bekijken
                  </DropdownItem>
                  <DropdownItem
                    key="edit"
                    startContent={<PencilSimple size={16} />}
                    onPress={() => onEdit(template)}
                  >
                    Bewerken
                  </DropdownItem>
                  <DropdownItem
                    key="delete"
                    className="text-danger"
                    color="danger"
                    startContent={<Trash size={16} />}
                    onPress={() => onDelete(template)}
                  >
                    Verwijderen
                  </DropdownItem>
                </DropdownMenu>
              </Dropdown>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export default DocumentTemplatesTable;
